import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { ButtonSize } from "@/components/Button/types";
import {
	DropdownWithCheckboxes,
	DropdownWithCheckboxesItem,
} from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";

import { PriorityFilterStatus } from "@/enum/list.enum";
import { ProspectInterest } from "@/enum/prospect.enum";

import { User } from "@/pages/Public/pages/Login/schema/login";
import { GetAllSendersUsersResponse } from "@/types/admin";
import { toPriorityFilterItems } from "@/utils/priorityFilterOptions";

import { ReplyTab } from "./inboxReplyTabs";

export interface InboxFilterDropdownsProps {
	/** Active reply tab — gates which sender dropdown (My/Team/All) is shown. */
	replyTab: ReplyTab;
	sendingUsers?: GetAllSendersUsersResponse;
	user?: User | null;
	doesSendingUsersHaveMe?: boolean;
	teamMembers: User[];
	selectedSenders: DropdownWithCheckboxesItem[];
	setSelectedSenders: (value: DropdownWithCheckboxesItem[]) => void;
	/** Lead-priority quick filter ("All qualified leads" etc.). */
	priorityFilterStatus: PriorityFilterStatus[];
	setPriorityFilterStatus: (value: PriorityFilterStatus[]) => void;
	/** Reply-sentiment quick filter (Interested / Not interested / …). */
	sentimentFilter: ProspectInterest[];
	setSentimentFilter: (value: ProspectInterest[]) => void;
	/** Reset pagination to page 1 whenever a filter changes. */
	onPageReset: () => void;
}

/** Sentiments offered by the reply quick filter, in display order (PREC-3599). */
const SENTIMENT_OPTIONS: ProspectInterest[] = [
	ProspectInterest.INTERESTED,
	ProspectInterest.BAD_TIMING,
	ProspectInterest.NEUTRAL,
	ProspectInterest.WRONG_PERSON,
	ProspectInterest.NOT_INTERESTED,
];

const SENTIMENT_ALL_ID = "all";

/**
 * The Inbox L3-nav filter dropdowns (PREC-3599): the existing per-tab sender selector plus the new
 * "qualified leads" (lead-priority) and reply-sentiment quick filters. Rendered as the
 * `MessagesFilterBar` `dropdowns` slot; styled to match the ReadyToSend filter dropdowns.
 */
export const InboxFilterDropdowns: FunctionComponent<InboxFilterDropdownsProps> = ({
	replyTab,
	sendingUsers,
	user,
	doesSendingUsersHaveMe,
	teamMembers,
	selectedSenders,
	setSelectedSenders,
	priorityFilterStatus,
	setPriorityFilterStatus,
	sentimentFilter,
	setSentimentFilter,
	onPageReset,
}) => {
	const { t } = useTranslation();

	const priorityValue: DropdownWithCheckboxesItem[] =
		priorityFilterStatus.length > 0
			? priorityFilterStatus.map((status) => ({
					title: t(`inbox.priorityFilterOptions.${status}`),
					id: status,
				}))
			: [
					{
						title: t(`inbox.priorityFilterOptions.${PriorityFilterStatus.ALL}`),
						id: PriorityFilterStatus.ALL,
					},
				];

	const sentimentValue: DropdownWithCheckboxesItem[] =
		sentimentFilter.length > 0
			? sentimentFilter.map((sentiment) => ({
					title: t(`inbox.sentimentFilterOptions.${sentiment}`),
					id: sentiment,
				}))
			: [{ title: t("inbox.sentimentFilterOptions.ALL"), id: SENTIMENT_ALL_ID }];

	const handlePrioritySelect = (values: DropdownWithCheckboxesItem[]) => {
		onPageReset();

		const addedValue = values.find(
			(val) => !priorityFilterStatus.includes(val.id as PriorityFilterStatus)
		);

		if (addedValue && addedValue.id === PriorityFilterStatus.ALL) {
			setPriorityFilterStatus([PriorityFilterStatus.ALL]);

			return;
		}

		if (addedValue && addedValue.id === PriorityFilterStatus.ALL_WITH_PRIORITY) {
			setPriorityFilterStatus([PriorityFilterStatus.ALL_WITH_PRIORITY]);

			return;
		}

		const newValues = values
			.filter(
				(val) =>
					val.id !== PriorityFilterStatus.ALL && val.id !== PriorityFilterStatus.ALL_WITH_PRIORITY
			)
			.map((val) => val.id as PriorityFilterStatus);

		setPriorityFilterStatus(newValues.length > 0 ? newValues : [PriorityFilterStatus.ALL]);
	};

	const handleSentimentSelect = (values: DropdownWithCheckboxesItem[]) => {
		onPageReset();

		// "All replies" is the empty state — selecting it (the newly-added item) clears the filter,
		// while picking a real sentiment drops the "All" pill and narrows to that sentiment.
		const previousIds: string[] =
			sentimentFilter.length > 0 ? sentimentFilter.map((s) => s as string) : [SENTIMENT_ALL_ID];
		const addedValue = values.find((val) => !previousIds.includes(String(val.id)));

		if (values.length === 0 || (addedValue && addedValue.id === SENTIMENT_ALL_ID)) {
			setSentimentFilter([]);

			return;
		}

		setSentimentFilter(
			values.filter((val) => val.id !== SENTIMENT_ALL_ID).map((val) => val.id as ProspectInterest)
		);
	};

	return (
		<>
			{replyTab === ReplyTab.ALL &&
				sendingUsers?.data?.length &&
				sendingUsers?.data?.length > 0 && (
					<div className="w-fit min-w-[120px] max-w-[220px]">
						<DropdownWithCheckboxes
							data={[
								{ id: "all", title: "All" },
								...(sendingUsers?.data?.map((sendingUser) => ({
									id: sendingUser.id,
									title:
										sendingUser.id === user?.id
											? `Me (${user?.firstName + " " + user?.lastName})`
											: sendingUser.firstName + " " + sendingUser.lastName,
								})) || []),
							]}
							defaultValue={[
								{
									id: doesSendingUsersHaveMe ? (user?.id ?? "all") : "all",
									title: doesSendingUsersHaveMe
										? `Me (${user?.firstName + " " + user?.lastName})`
										: `All`,
								},
							]}
							floating={true}
							handleSelect={(values: DropdownWithCheckboxesItem[]): void => {
								onPageReset();

								const addedValue = values.find(
									(val) => !selectedSenders.map((s) => s.id).includes(val.id)
								);

								const newValues = values.filter((val) => val.id !== "all");

								const removedValue = selectedSenders.find(
									(val) => !values.map((s) => s.id).includes(val.id)
								);

								if (addedValue && addedValue.id === "all") {
									setSelectedSenders([
										{ id: "all", title: "All" },
										...(sendingUsers?.data?.map((sendingUser) => ({
											id: sendingUser.id,
											title:
												sendingUser.id === user?.id
													? `Me (${user?.firstName + " " + user?.lastName})`
													: sendingUser.firstName + " " + sendingUser.lastName,
										})) || []),
									]);
								} else if (removedValue && removedValue.id === "all") {
									setSelectedSenders([
										{
											id: user?.id || "",
											title: `Me (${user?.firstName + " " + user?.lastName})`,
										},
									]);
								} else {
									setSelectedSenders(newValues);
								}
							}}
							size={ButtonSize.XS}
							title="Inbox"
							value={selectedSenders}
						/>
					</div>
				)}
			{replyTab === ReplyTab.TEAM && teamMembers.length > 0 && (
				<div className="w-fit min-w-[120px] max-w-[220px]">
					<DropdownWithCheckboxes
						data={[
							{ id: "all", title: "All" },
							...teamMembers.map((member) => ({
								id: member.id,
								title: `${member.firstName} ${member.lastName}`,
							})),
						]}
						defaultValue={[{ id: "all", title: "All" }]}
						floating={true}
						handleSelect={(values: DropdownWithCheckboxesItem[]): void => {
							onPageReset();

							const addedAll =
								values.some((val) => val.id === "all") &&
								!selectedSenders.some((s) => s.id === "all");

							if (addedAll) {
								setSelectedSenders([
									{ id: "all", title: "All" },
									...teamMembers.map((member) => ({
										id: member.id,
										title: `${member.firstName} ${member.lastName}`,
									})),
								]);
							} else {
								setSelectedSenders(values.filter((val) => val.id !== "all"));
							}
						}}
						size={ButtonSize.XS}
						title="Team"
						value={selectedSenders}
					/>
				</div>
			)}
			<div className="w-fit min-w-[120px] max-w-[220px]">
				<DropdownWithCheckboxes
					data={toPriorityFilterItems((status) => t(`inbox.priorityFilterOptions.${status}`))}
					defaultValue={priorityValue}
					floating={true}
					handleSelect={handlePrioritySelect}
					size={ButtonSize.XS}
					testId="inbox-priority-filter"
					title={t("inbox.quickFilters.priorityTitle")}
					value={priorityValue}
				/>
			</div>
			<div className="w-fit min-w-[120px] max-w-[220px]">
				<DropdownWithCheckboxes
					data={[
						{ title: t("inbox.sentimentFilterOptions.ALL"), id: SENTIMENT_ALL_ID },
						...SENTIMENT_OPTIONS.map((sentiment) => ({
							title: t(`inbox.sentimentFilterOptions.${sentiment}`),
							id: sentiment,
						})),
					]}
					defaultValue={sentimentValue}
					floating={true}
					handleSelect={handleSentimentSelect}
					size={ButtonSize.XS}
					testId="inbox-sentiment-filter"
					title={t("inbox.quickFilters.sentimentTitle")}
					value={sentimentValue}
				/>
			</div>
		</>
	);
};
